/**
 * Route Keys - Canonical method+path definitions for all API routes
 *
 * Every route in the API must have a corresponding entry here.
 * The inventory check ensures contracts ↔ registered routes stay in sync.
 */
// =============================================================================
// Route Keys
// =============================================================================
export const ROUTE_KEYS = {
    // =========================================================================
    // Auth Routes (mounted at /auth)
    // =========================================================================
    AUTH_RESERVE: 'POST /auth/reserve',
    AUTH_CHECK_USERNAME: 'GET /auth/check/:username',
    AUTH_CHALLENGE: 'POST /auth/challenge',
    AUTH_REGISTER: 'POST /auth/register',
    AUTH_PROVE: 'POST /auth/prove',
    AUTH_LOGOUT: 'POST /auth/logout',
    // =========================================================================
    // Session Routes (mounted at /auth)
    // =========================================================================
    SESSIONS_LIST: 'GET /auth/sessions',
    SESSIONS_DELETE: 'DELETE /auth/sessions/:id',
    SESSIONS_REVOKE_OTHERS: 'POST /auth/sessions/revoke-others',
    // =========================================================================
    // Account Routes (mounted at /auth)
    // =========================================================================
    ACCOUNT_DELETE: 'DELETE /auth/account',
    ACCOUNT_EXPORT: 'GET /auth/export',
    // =========================================================================
    // Inbox Routes (mounted at /inbox)
    // =========================================================================
    INBOX_LIST: 'GET /inbox',
    INBOX_GET: 'GET /inbox/:id',
    // =========================================================================
    // Device Routes (mounted at /devices)
    // =========================================================================
    DEVICES_AUTH_CHECK: 'GET /devices/auth-check',
    DEVICES_PAIR_REQUEST: 'POST /devices/pair/request',
    DEVICES_PAIR_CLAIM: 'POST /devices/pair/claim',
    DEVICES_FINALIZE_READER: 'POST /devices/finalize-reader',
    DEVICES_REQUESTS: 'GET /devices/requests',
    DEVICES_STREAM: 'GET /devices/stream',
    DEVICES_LIST: 'GET /devices',
    DEVICES_REVOKE: 'DELETE /devices/:id',
    // =========================================================================
    // Billing Routes (mounted at /billing)
    // =========================================================================
    BILLING_CHECKOUT: 'POST /billing/checkout',
    BILLING_WEBHOOK: 'POST /billing/webhook',
    BILLING_DEV_SIMULATE: 'POST /billing/dev-simulate',
    BILLING_VERIFY: 'POST /billing/verify',
    // =========================================================================
    // Identity Routes (mounted at /identity)
    // =========================================================================
    IDENTITY_STATUS: 'GET /identity/status',
    // =========================================================================
    // Meta Routes (mounted at /meta)
    // =========================================================================
    META_CONTRACT: 'GET /meta/contract',
    META_ROUTES: 'GET /__meta/routes',
    // =========================================================================
    // Log Routes (mounted at /log)
    // =========================================================================
    LOG_INGEST: 'POST /log',
    // =========================================================================
    // Admin Routes (mounted at /admin)
    // =========================================================================
    ADMIN_PROVISION: 'POST /admin/identities/provision',
    ADMIN_LIST_IDENTITIES: 'GET /admin/identities',
    ADMIN_STATS: 'GET /admin/stats',
    ADMIN_GET_IDENTITY: 'GET /admin/identities/:username',
    ADMIN_DELETE_IDENTITY: 'DELETE /admin/identities/:username',
    ADMIN_ACTIVATE_IDENTITY: 'POST /admin/identities/:username/activate',
    ADMIN_RECONCILE_IDENTITY: 'POST /admin/identities/:username/reconcile',
    ADMIN_LOGS: 'GET /admin/logs', // Added because it exists in implementation
    // =========================================================================
    // Root Routes (mounted at /)
    // =========================================================================
    ROOT: 'GET /',
    HEALTH: 'GET /health',
};
// =============================================================================
// Route Metadata
// =============================================================================
import * as v from 'valibot';
import * as validators from './validators';
export const ROUTE_META = {
    // Auth
    [ROUTE_KEYS.AUTH_RESERVE]: {
        auth: 'public',
        smoke: true,
        danger: 'mutation',
        requestSchema: validators.ReserveUsernameSchema,
        responseSchema: validators.ReserveUsernameResponseSchema,
    },
    [ROUTE_KEYS.AUTH_CHECK_USERNAME]: {
        auth: 'public',
        smoke: true,
        requestSchema: v.object({ username: v.string() }),
        responseSchema: validators.CheckUsernameResponseSchema,
    },
    [ROUTE_KEYS.AUTH_CHALLENGE]: {
        auth: 'public',
        smoke: true,
        requestSchema: validators.AuthChallengeSchema,
        responseSchema: validators.AuthChallengeResponseSchema,
    },
    [ROUTE_KEYS.AUTH_REGISTER]: {
        auth: 'public',
        danger: 'mutation',
        requestSchema: validators.RegisterSchema,
        responseSchema: validators.RegisterResponseSchema,
        idempotency: 'optional', // Already wrapped manually
    },
    [ROUTE_KEYS.AUTH_PROVE]: {
        auth: 'public',
        smoke: true,
        requestSchema: validators.AuthProveSchema,
        responseSchema: validators.AuthProveResponseSchema,
    },
    [ROUTE_KEYS.AUTH_LOGOUT]: {
        auth: 'user',
        requestSchema: validators.LogoutSchema,
        responseSchema: validators.LogoutResponseSchema,
    },
    // Sessions
    [ROUTE_KEYS.SESSIONS_LIST]: {
        auth: 'user',
        responseSchema: validators.SessionListResponseSchema,
    },
    [ROUTE_KEYS.SESSIONS_DELETE]: {
        auth: 'user',
        danger: 'mutation',
        requestSchema: v.object({ id: v.string() }),
        responseSchema: validators.SuccessResponseSchema,
    },
    [ROUTE_KEYS.SESSIONS_REVOKE_OTHERS]: {
        auth: 'user',
        danger: 'mutation',
        responseSchema: validators.SuccessResponseSchema,
    },
    // Account
    [ROUTE_KEYS.ACCOUNT_DELETE]: {
        auth: 'user',
        danger: 'mutation',
        responseSchema: validators.SuccessResponseSchema,
    },
    [ROUTE_KEYS.ACCOUNT_EXPORT]: {
        auth: 'user',
        responseSchema: validators.AccountExportResponseSchema,
    },
    // Inbox
    [ROUTE_KEYS.INBOX_LIST]: {
        auth: 'user',
        requestSchema: validators.InboxListRequestSchema,
        responseSchema: validators.InboxListResponseSchema,
    },
    [ROUTE_KEYS.INBOX_GET]: {
        auth: 'user',
        requestSchema: v.object({ id: v.string() }),
        responseSchema: validators.InboxGetResponseSchema,
    },
    // Devices
    [ROUTE_KEYS.DEVICES_AUTH_CHECK]: {
        auth: 'device',
        smoke: true,
        responseSchema: validators.DeviceAuthCheckResponseSchema,
    },
    [ROUTE_KEYS.DEVICES_PAIR_REQUEST]: {
        auth: 'public',
        smoke: true,
        requestSchema: validators.DevicePairRequestSchema,
        responseSchema: validators.DevicePairResponseSchema,
    },
    [ROUTE_KEYS.DEVICES_PAIR_CLAIM]: {
        auth: 'public',
        danger: 'mutation',
        requestSchema: validators.DeviceClaimRequestSchema,
        responseSchema: validators.DeviceClaimResponseSchema,
    },
    [ROUTE_KEYS.DEVICES_FINALIZE_READER]: {
        auth: 'user',
        danger: 'mutation',
        requestSchema: validators.DeviceFinalizeRequestSchema,
        responseSchema: validators.DeviceFinalizeResponseSchema,
    },
    [ROUTE_KEYS.DEVICES_REQUESTS]: {
        auth: 'user',
        responseSchema: validators.DeviceRequestsResponseSchema,
    },
    [ROUTE_KEYS.DEVICES_STREAM]: {
        auth: 'user',
        responseSchema: validators.DeviceStreamResponseSchema,
    },
    [ROUTE_KEYS.DEVICES_LIST]: {
        auth: 'user',
        responseSchema: validators.DeviceListResponseSchema,
    },
    [ROUTE_KEYS.DEVICES_REVOKE]: {
        auth: 'user',
        danger: 'mutation',
        requestSchema: v.object({ id: v.string() }),
        responseSchema: validators.SuccessResponseSchema,
    },
    // Billing
    [ROUTE_KEYS.BILLING_CHECKOUT]: {
        auth: 'user',
        danger: 'mutation',
        idempotency: 'required', // Money changes state
        requestSchema: validators.CheckoutSchema,
        responseSchema: validators.BillingCheckoutResponseSchema,
    },
    [ROUTE_KEYS.BILLING_WEBHOOK]: {
        auth: 'public',
        danger: 'mutation',
        responseSchema: v.string(),
    }, // Text response
    [ROUTE_KEYS.BILLING_DEV_SIMULATE]: {
        auth: 'admin',
        danger: 'mutation',
        requestSchema: v.object({ username: v.string() }),
        responseSchema: validators.SuccessResponseSchema,
    },
    [ROUTE_KEYS.BILLING_VERIFY]: {
        auth: 'user', // User triggers check
        danger: 'mutation',
        requestSchema: v.object({ paymentId: v.string() }),
        responseSchema: validators.SuccessResponseSchema,
    },
    // Identity
    [ROUTE_KEYS.IDENTITY_STATUS]: {
        auth: 'user',
        requestSchema: v.optional(v.object({ include_crypto: v.optional(v.string()) })),
        responseSchema: validators.IdentityStatusResponseSchema,
    },
    // Meta
    [ROUTE_KEYS.META_CONTRACT]: { auth: 'public', smoke: true, responseSchema: v.any() },
    // Log
    [ROUTE_KEYS.LOG_INGEST]: {
        auth: 'public',
        requestSchema: validators.LogEntrySchema,
        responseSchema: validators.SuccessResponseSchema,
    },
    // Admin
    [ROUTE_KEYS.ADMIN_PROVISION]: {
        auth: 'admin',
        danger: 'mutation',
        requestSchema: validators.ProvisionIdentitySchema,
        responseSchema: validators.SuccessResponseSchema,
    },
    [ROUTE_KEYS.ADMIN_LIST_IDENTITIES]: {
        auth: 'admin',
        requestSchema: validators.AdminListIdentitiesRequestSchema,
        responseSchema: validators.AdminListIdentitiesResponseSchema,
    },
    [ROUTE_KEYS.ADMIN_STATS]: {
        auth: 'admin',
        responseSchema: validators.AdminStatsResponseSchema,
    },
    [ROUTE_KEYS.ADMIN_GET_IDENTITY]: {
        auth: 'admin',
        requestSchema: v.object({ username: v.string() }),
        responseSchema: validators.AdminGetIdentityResponseSchema,
    },
    [ROUTE_KEYS.ADMIN_DELETE_IDENTITY]: {
        auth: 'admin',
        danger: 'mutation',
        requestSchema: v.object({ username: v.string() }),
        responseSchema: validators.SuccessResponseSchema,
    },
    [ROUTE_KEYS.ADMIN_ACTIVATE_IDENTITY]: {
        auth: 'admin',
        danger: 'mutation',
        requestSchema: v.object({ username: v.string() }),
        responseSchema: validators.SuccessResponseSchema,
    },
    [ROUTE_KEYS.ADMIN_RECONCILE_IDENTITY]: {
        auth: 'admin',
        danger: 'mutation',
        requestSchema: v.object({ username: v.string() }),
        responseSchema: validators.SuccessResponseSchema,
    },
    [ROUTE_KEYS.ADMIN_LOGS]: {
        auth: 'admin',
        requestSchema: validators.AdminLogsRequestSchema,
        responseSchema: validators.AdminLogsResponseSchema,
    },
    // Root
    [ROUTE_KEYS.ROOT]: {
        auth: 'public',
        smoke: true,
        responseSchema: v.object({
            service: v.string(),
            version: v.string(),
            hash: v.string(),
        }),
    },
    [ROUTE_KEYS.HEALTH]: {
        auth: 'public',
        smoke: true,
        responseSchema: v.object({
            status: v.string(),
            ts: v.number(),
            db_latency_ms: v.optional(v.number()),
            build_sha: v.optional(v.string()),
        }),
    },
    [ROUTE_KEYS.META_ROUTES]: {
        // Explicitly typed for smoke tests
        auth: 'public',
        smoke: true,
        responseSchema: v.object({
            contract: v.any(),
            registry: v.any(),
        }),
    },
};
// =============================================================================
// Utility Functions
// =============================================================================
/**
 * Parse a route key into method and path
 */
export function parseRouteKey(key) {
    const spaceIndex = key.indexOf(' ');
    return {
        method: key.slice(0, spaceIndex),
        path: key.slice(spaceIndex + 1),
    };
}
/**
 * Get all route keys for a given auth tier
 */
export function getRoutesByAuth(tier) {
    return Object.values(ROUTE_KEYS).filter((key) => ROUTE_META[key].auth === tier);
}
//# sourceMappingURL=route-defs.js.map